import React, { type FC, useEffect, useRef, useState } from 'react';
import { useEmailTemplate, useSaveEmailClient, useSaveEmailTemplate } from '../../../hooks/api/EmailTemplate/useEmailTemplate';
import { getErrorMessages } from '../../../utility/team';
import { insertAtCursor, variableTag } from '../../../utility/listingEmail';
import EmailDocumentsSection from './EmailDocumentsSection';

const primaryButton = 'rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400';
const secondaryButton = 'rounded-md px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50';

// Configuration > Email Template. The letter that goes in the email we draft to a listing agent.
// Click a variable to put it in at the cursor; it is filled in when the draft is made.
const EmailTemplatePage: FC = () => {
  const { template, isLoading, error } = useEmailTemplate();
  const save = useSaveEmailTemplate();
  const saveClient = useSaveEmailClient();
  const [ body, setBody ] = useState('');
  const [ saved, setSaved ] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (template) setBody(template.body);
  }, [ template ]);

  const addVariable = (key: string) => {
    const el = textarea.current;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    const next = insertAtCursor(body, variableTag(key), start, end);
    setBody(next.text);
    setSaved(false);
    // put the cursor back after the inserted variable once React has re-rendered
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(next.cursor, next.cursor);
    });
  };

  const handleSave = async (text: string) => {
    setSaved(false);
    try {
      const response = await save.mutateAsync(text);
      setBody(response.data.body);
      setSaved(true);
    } catch {
      // the server's message is shown below from the mutation state
    }
  };

  return (
    <div className='px-2'>
      <h1 className='text-base font-semibold leading-6 text-gray-900'>Email Template</h1>
      <p className='mt-2 max-w-3xl text-sm text-gray-700'>
        This is the letter in the email drafted to a property's listing agent from the Final Decision page (Actions &gt; Email
        Listing Agent). The draft is addressed to the agent, the contract is attached, and the subject is always{' '}
        <span className='font-medium'>address - Offer</span>. Nothing is sent for you: you open the draft in your email app, review it and send it.
      </p>

      {isLoading && <p className='mt-6 text-sm text-gray-600'>Loading...</p>}
      {error && <p className='mt-6 text-sm text-red-600'>{getErrorMessages(error)[0]}</p>}

      {template && (
        <fieldset className='mt-6 max-w-3xl' data-tag='email-client-choice'>
          <legend className='text-sm font-semibold text-gray-900'>Which email app do you use?</legend>
          <div className='mt-2 space-y-2'>
            {template.email_clients.map((client) => (
              <label key={client.key} className='flex items-start gap-x-2 text-sm text-gray-700'>
                <input
                  type='radio'
                  name='email_client'
                  className='mt-1'
                  value={client.key}
                  checked={template.email_client === client.key}
                  disabled={saveClient.isPending}
                  onChange={() => saveClient.mutate(client.key)}
                />
                <span>
                  <span className='font-medium text-gray-900'>{client.label}</span>
                  <span className='block text-gray-500'>{client.hint}</span>
                </span>
              </label>
            ))}
          </div>
          {saveClient.error && (
            <ul role='alert' className='mt-2 text-sm text-red-600'>
              {getErrorMessages(saveClient.error).map((m) => <li key={m}>{m}</li>)}
            </ul>
          )}
        </fieldset>
      )}

      {template && (
        <div className='mt-8 max-w-3xl'>
          <p className='text-sm font-medium text-gray-900'>Add details (click to insert at the cursor)</p>
          <div className='mt-2 flex flex-wrap gap-2'>
            {template.variables.map((variable) => (
              <button
                key={variable.key}
                type='button'
                title={variable.label}
                className='rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100'
                onClick={() => addVariable(variable.key)}
              >
                {variableTag(variable.key)}
              </button>
            ))}
          </div>

          <label htmlFor='email_template_body' className='mt-6 block text-sm font-medium text-gray-900'>Your letter</label>
          <textarea
            id='email_template_body'
            ref={textarea}
            rows={14}
            className='mt-1 block w-full rounded-md border-0 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600'
            value={body}
            onChange={(e) => { setBody(e.target.value); setSaved(false); }}
          />

          {save.error && (
            <ul role='alert' className='mt-2 text-sm text-red-600'>
              {getErrorMessages(save.error).map((m) => <li key={m}>{m}</li>)}
            </ul>
          )}
          {saved && <p className='mt-2 text-sm text-green-600'>Saved.</p>}

          <div className='mt-4 flex gap-x-3'>
            <button type='button' className={primaryButton} disabled={save.isPending || !body.trim()} onClick={() => handleSave(body)}>
              {save.isPending ? 'Saving...' : 'Save'}
            </button>
            <button type='button' className={secondaryButton} disabled={save.isPending} onClick={() => handleSave('')}>
              Reset to default
            </button>
          </div>
        </div>
      )}

      <EmailDocumentsSection />
    </div>
  );
};

export default EmailTemplatePage;
